import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Typography,
  Stack,
  Chip,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Popover,
  TextField,
  Button,
  alpha,
} from '@mui/material';
import Chart from 'react-apexcharts';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBalanceSheet, fetchProfitLoss } from '../../store/reportStore/reportThunk';
import { ApiStatus } from '../../utils/api.types';
import { Utils } from '../../utils/common-utils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ACCENT = '#84cc16';
const ACCENT_LIGHT = '#d9f99d';
const ACCENT_SOFT = '#ecfccb';
const BG = '#f5f7fa';
const SURFACE = '#ffffff';
const TEXT = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#eef0f3';

const cardSx = {
  borderRadius: 4,
  border: `1px solid ${BORDER}`,
  boxShadow: 'none',
  backgroundColor: SURFACE,
};

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

const topN = (groups: any[] | undefined, n = 5) => {
  if (!groups || !groups.length) return { names: [], values: [] };
  const sorted = [...groups].sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, n);
  return {
    names: sorted.map((g) => g.name || g.accountingCode || '—'),
    values: sorted.map((g) => Number(g.total || 0)),
  };
};

const CoaDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((s) => s.auth);
  const accountId = selectedAccount?._id;
  const { balanceSheetData, profitLossData, status, errorMessage } = useAppSelector((s) => s.reports);

  const [period, setPeriod] = useState('fy');
  const [isLoading, setIsLoading] = useState(false);
  const [calAnchor, setCalAnchor] = useState<HTMLElement | null>(null);
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);
  const [appliedRange, setAppliedRange] = useState<{ start: Date; end: Date } | null>(null);

  useEffect(() => {
    if (!accountId) return;
    const daysMap: Record<string, number> = { '7days': 7, '30days': 30, '90days': 90, '365days': 365 };
    let start: Date, end: Date;
    if (appliedRange) {
      start = appliedRange.start;
      end = appliedRange.end;
    } else if (period === 'fy') {
      const now = new Date();
      const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      start = new Date(fyStartYear, 3, 1);
      end = new Date(fyStartYear + 1, 2, 31);
    } else {
      const days = daysMap[period] || 30;
      end = new Date();
      start = new Date();
      start.setDate(end.getDate() - days + 1);
    }
    const params = `start=${fmtDate(start)}&end=${fmtDate(end)}`;
    setIsLoading(true);
    Promise.all([
      dispatch(fetchBalanceSheet({ accountId, startDate: '', endDate: '', params })),
      dispatch(fetchProfitLoss({ accountId, startDate: '', endDate: '', params })),
    ]).finally(() => setIsLoading(false));
  }, [accountId, period, appliedRange, dispatch]);

  const handlePeriodChange = (_e: any, p: string | null) => {
    if (p) {
      setPeriod(p);
      setAppliedRange(null);
    }
  };

  const applyCustomRange = () => {
    if (customStart && customEnd) {
      setAppliedRange({ start: customStart, end: customEnd });
      setCalAnchor(null);
    }
  };

  const clearCustomRange = () => {
    setAppliedRange(null);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const rangeLabel = appliedRange
    ? `${appliedRange.start.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} – ${appliedRange.end.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`
    : '';

  const pl: any = profitLossData || {};
  const bs: any = balanceSheetData || {};

  const totalRevenue = Number(pl?.summary?.totalRevenue ?? pl?.revenue?.totalRevenue ?? 0);
  const totalCOGS = Number(pl?.summary?.totalCOGS ?? pl?.cogs?.total ?? 0);
  const grossProfit = Number(pl?.summary?.grossProfit ?? pl?.grossProfit ?? 0);
  const totalOpEx = Number(pl?.summary?.totalOperatingExpenses ?? pl?.operatingExpenses?.total ?? 0);
  const opProfit = Number(pl?.summary?.operatingProfit ?? pl?.operatingProfit ?? 0);
  const totalOtherEx = Number(pl?.summary?.totalOtherExpenses ?? pl?.otherExpenses?.total ?? 0);
  const netProfit = Number(pl?.summary?.netProfit ?? pl?.netProfitAfterTax ?? 0);
  const netMargin = Number(pl?.summary?.netProfitMargin || 0);
  const totalExpenses = totalCOGS + totalOpEx + totalOtherEx;

  const totalAssets = Number(bs?.assets?.total || 0);
  const totalLiabilities = Number(bs?.liabilities?.total || 0);
  const totalEquity = Number(bs?.equity?.total || 0);
  const isBalanced = bs?.balanceCheck?.isBalanced;
  const balanceDiff = Number(bs?.balanceCheck?.difference || 0);


  const loading = isLoading || status === ApiStatus.LOADING;

  // Flatten group → ledger rows (handles subGroups + direct ledgers)
  const flattenLedgers = (groups: any[] | undefined): any[] => {
    const out: any[] = [];
    (groups || []).forEach((g) => {
      const groupName = g.name || g.accountingCode || '—';
      (g.subGroups || []).forEach((sg: any) => {
        (sg.ledgers || []).forEach((l: any) => {
          out.push({
            group: groupName,
            name: l.name || '—',
            code: l.accountingCode || '',
            debit: Number(l.debit || 0),
            credit: Number(l.credit || 0),
            balance: Number(l.balance ?? l.amount ?? 0),
          });
        });
      });
      (g.ledgers || []).forEach((l: any) => {
        out.push({
          group: groupName,
          name: l.name || '—',
          code: l.accountingCode || '',
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
          balance: Number(l.balance ?? l.amount ?? 0),
        });
      });
    });
    return out;
  };

  const MetricCard = ({
    title,
    value,
    sub,
    icon,
    accent = ACCENT,
    highlight = false,
  }: {
    title: string;
    value: string | number;
    sub?: { positive: boolean; label: string } | null;
    icon: React.ReactNode;
    accent?: string;
    highlight?: boolean;
  }) => (
    <Card
      sx={{
        ...cardSx,
        backgroundColor: highlight ? ACCENT_SOFT : SURFACE,
        borderColor: highlight ? ACCENT_LIGHT : BORDER,
        height: '100%',
        minHeight: 110,
        transition: 'transform .2s ease, box-shadow .2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px -12px rgba(15,23,42,0.12)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 1.75,
          '&:last-child': { pb: 1.75 },
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.3, mb: 0.75 }} noWrap>
              {title}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: TEXT, lineHeight: 1.2, fontSize: '1rem' }} noWrap>
              {loading ? <Skeleton width={70} /> : value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: highlight ? '#ffffff' : alpha(accent, 0.12),
              color: accent,
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 17, color: accent } })}
          </Box>
        </Stack>
        <Box sx={{ height: 20, mt: 1, display: 'flex', alignItems: 'center' }}>
          {sub ? (
            <Chip
              size="small"
              icon={sub.positive
                ? <TrendingUpIcon sx={{ fontSize: 11, color: '#16a34a !important' }} />
                : <TrendingDownIcon sx={{ fontSize: 11, color: '#dc2626 !important' }} />}
              label={sub.label}
              sx={{
                height: 18,
                fontSize: '0.62rem',
                fontWeight: 600,
                color: sub.positive ? '#16a34a' : '#dc2626',
                backgroundColor: sub.positive ? '#dcfce7' : '#fee2e2',
                '& .MuiChip-icon': { ml: '3px', mr: '-2px' },
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ) : (
            <Typography sx={{ color: MUTED, fontSize: '0.62rem', fontStyle: 'italic' }}>—</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const baseAxis = {
    chart: { toolbar: { show: false }, fontFamily: 'inherit' },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    tooltip: { y: { formatter: (v: number) => Utils.formatNumber(v, true, false) } },
  };

  const plFullNames = [
    'Total Revenue',
    'Cost of Goods Sold',
    'Gross Profit',
    'Operating Expenses',
    'Operating Profit',
    'Other Expenses',
    'Net Profit',
  ];

  // Build vertical ledger-breakdown HTML for any group list
  const TT_FONT = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const buildLedgerListTooltip = (title: string, totalVal: number, color: string, groups: any[] | undefined) => {
    const ledgers = flattenLedgers(groups);
    const rows = ledgers.map((l) => `
      <div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px dashed #eef0f3;font-family:${TT_FONT};">
        <div style="flex:1;min-width:0;">
          <div style="font-size:11px;font-weight:600;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-family:${TT_FONT};">${l.name}</div>
          ${l.group ? `<div style="font-size:9px;color:#64748b;font-family:${TT_FONT};">${l.group}</div>` : ''}
        </div>
        <div style="text-align:right;margin-left:10px;min-width:90px;">
          <div style="font-size:11px;font-weight:700;color:${color};font-family:${TT_FONT};">${Utils.formatNumber(l.balance || l.debit || l.credit || 0, true, false)}</div>
          ${l.debit > 0 || l.credit > 0 ? `<div style="font-size:9px;color:#64748b;font-family:${TT_FONT};">Dr ${Utils.formatNumber(l.debit, true, false)} · Cr ${Utils.formatNumber(l.credit, true, false)}</div>` : ''}
        </div>
      </div>`).join('');
    return `
      <div style="padding:10px 12px;font-family:${TT_FONT};background:#fff;min-width:260px;max-width:380px;max-height:240px;overflow-y:auto;border-radius:8px;box-shadow:0 4px 14px rgba(0,0,0,0.08);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;border-bottom:2px solid ${color};padding-bottom:6px;font-family:${TT_FONT};position:sticky;top:0;background:#fff;">
          <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;"></span>
          <b style="color:#0f172a;font-size:12px;flex:1;font-family:${TT_FONT};">${title}</b>
          <span style="color:${color};font-weight:800;font-size:12px;font-family:${TT_FONT};">${Utils.formatNumber(totalVal, true, false)}</span>
        </div>
        ${ledgers.length === 0 ? `<div style="font-size:10px;color:#64748b;padding:4px 0;font-family:${TT_FONT};">No ledger detail</div>` : rows}
      </div>`;
  };

  // Map P&L Flow bar index → source group list for ledger drilldown
  const plBucketGroups = (idx: number): { groups: any[] | undefined; title: string; computed?: boolean } => {
    switch (idx) {
      case 0: return { groups: [...(pl?.revenue?.salesIncome?.breakdown || []), ...(pl?.revenue?.otherIncome?.breakdown || [])], title: 'Total Revenue' };
      case 1: return { groups: pl?.cogs?.breakdown, title: 'Cost of Goods Sold' };
      case 2: return { groups: undefined, title: 'Gross Profit (Revenue − COGS)', computed: true };
      case 3: return { groups: pl?.operatingExpenses?.breakdown, title: 'Operating Expenses' };
      case 4: return { groups: undefined, title: 'Operating Profit (Gross − OpEx)', computed: true };
      case 5: return { groups: pl?.otherExpenses?.breakdown, title: 'Other Expenses' };
      case 6: return { groups: undefined, title: 'Net Profit (Op − Other + Round Off)', computed: true };
      default: return { groups: undefined, title: '' };
    }
  };

  const plBarOptions = useMemo(
    () => ({
      ...baseAxis,
      chart: { ...baseAxis.chart, type: 'bar' as const, height: 260 },
      plotOptions: {
        bar: {
          columnWidth: '55%',
          borderRadius: 6,
          borderRadiusApplication: 'end' as const,
          distributed: true,
          dataLabels: { position: 'top' as const },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (v: number) => Utils.formatNumber(v, true, false),
        offsetY: -20,
        style: { fontSize: '10px', fontWeight: 600, colors: [TEXT] },
      },
      xaxis: {
        categories: ['Revenue', 'COGS', 'Gross', 'Op Exp', 'Op Profit', 'Other Exp', 'Net'],
        labels: { style: { colors: MUTED, fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: MUTED, fontSize: '11px' }, formatter: (v: number) => Utils.formatNumber(v, true, false) } },
      colors: [ACCENT, '#ef4444', '#3b82f6', '#ef4444', '#3b82f6', '#ef4444', netProfit >= 0 ? ACCENT : '#ef4444'],
      tooltip: {
        custom: ({ dataPointIndex, w }: any) => {
          const val = w?.config?.series?.[0]?.data?.[dataPointIndex] || 0;
          const color = w?.config?.colors?.[dataPointIndex] || TEXT;
          const meta = plBucketGroups(dataPointIndex);
          if (meta.computed) {
            return `<div style="padding:8px 12px;font-size:11px;background:#fff;border-radius:8px;min-width:200px;font-family:${TT_FONT};box-shadow:0 4px 14px rgba(0,0,0,0.08);">
              <b style="color:${TEXT};font-size:12px;font-family:${TT_FONT};">${meta.title}</b><br/>
              <span style="color:${color};font-weight:800;font-size:13px;font-family:${TT_FONT};">${Utils.formatNumber(val, true, false)}</span>
              <div style="font-size:9px;color:#64748b;margin-top:4px;font-style:italic;font-family:${TT_FONT};">Derived value — no direct ledger</div>
            </div>`;
          }
          return buildLedgerListTooltip(meta.title || plFullNames[dataPointIndex] || '', val, color, meta.groups);
        },
      },
    }),
    [netProfit]
  );

  const plBarSeries = [{ name: 'Amount', data: [totalRevenue, totalCOGS, grossProfit, totalOpEx, opProfit, totalOtherEx, netProfit] }];

  const bsDonutOptions = {
    chart: { type: 'donut' as const, fontFamily: 'inherit' },
    labels: ['Assets', 'Liabilities', 'Equity'],
    colors: [ACCENT, '#ef4444', '#3b82f6'],
    legend: { position: 'bottom' as const, fontSize: '11px', labels: { colors: MUTED }, markers: { width: 8, height: 8, radius: 12 } as any, itemMargin: { horizontal: 6, vertical: 2 } },
    dataLabels: { enabled: true, style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] }, formatter: (val: number) => `${val.toFixed(0)}%` },
    stroke: { width: 2, colors: ['#fff'] },
    plotOptions: { pie: { donut: { size: '60%', labels: { show: false } } } },
    tooltip: { y: { formatter: (v: number) => Utils.formatNumber(v, true, false) } },
  };
  const bsDonutSeries = [totalAssets, totalLiabilities, totalEquity];

  const revenueTop = topN(pl?.revenue?.salesIncome?.breakdown);
  const expenseTop = topN(pl?.operatingExpenses?.breakdown);

  const horizBar = (categories: string[], color: string) => ({
    ...baseAxis,
    chart: { ...baseAxis.chart, type: 'bar' as const },
    plotOptions: {
      bar: { horizontal: true, barHeight: '60%', borderRadius: 4, borderRadiusApplication: 'end' as const, distributed: false, dataLabels: { position: 'top' as const } },
    },
    dataLabels: {
      enabled: true,
      textAnchor: 'start' as const,
      formatter: (v: number) => Utils.formatNumber(v, true, false),
      style: { fontSize: '10px', fontWeight: 600, colors: [TEXT] },
      offsetX: 8,
    },
    xaxis: { categories, labels: { style: { colors: MUTED, fontSize: '10px' }, formatter: (v: any) => Utils.formatNumber(Number(v), true, false) }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { colors: TEXT, fontSize: '10px' } } },
    colors: [color],
  });

  const heatmapOpts = (color: string, maxVal: number, names: string[]) => ({
    chart: { type: 'heatmap' as const, toolbar: { show: false }, fontFamily: 'inherit' },
    dataLabels: {
      enabled: true,
      formatter: (val: number, opt: any) => {
        const name = names[opt.dataPointIndex] || '';
        return [name, Utils.formatNumber(val, true, false)] as any;
      },
      style: { fontSize: '12px', fontWeight: 700, colors: ['#fff'] },
      offsetY: -2,
    },
    stroke: { width: 3, colors: ['#fff'] },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { show: false, labels: { show: false } },
    grid: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    legend: { show: false },
    plotOptions: {
      heatmap: {
        radius: 8,
        enableShades: false,
        colorScale: {
          ranges: [
            { from: 0, to: maxVal * 0.25, name: 'Low', color: color + '55' },
            { from: maxVal * 0.25 + 0.01, to: maxVal * 0.5, name: 'Mid-Low', color: color + '88' },
            { from: maxVal * 0.5 + 0.01, to: maxVal * 0.75, name: 'Mid-High', color: color + 'bb' },
            { from: maxVal * 0.75 + 0.01, to: maxVal, name: 'High', color },
          ],
        },
      },
    },
    tooltip: {
      y: { formatter: (v: number) => Utils.formatNumber(v, true, false) },
      x: { formatter: (_v: any, opt: any) => names[opt?.dataPointIndex] || '' },
    },
  });

  const heatmapSeries = (names: string[], values: number[]) => [{
    name: 'Amount',
    data: names.map((n, i) => ({ x: n, y: values[i] || 0 })),
  }];

  const bubbleOpts = (categories: string[], palette: string[]) => ({
    chart: { type: 'bubble' as const, toolbar: { show: false }, fontFamily: 'inherit' },
    dataLabels: {
      enabled: true,
      formatter: (_val: any, opt: any) => categories[opt.seriesIndex] || '',
      style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] },
      dropShadow: { enabled: false },
    },
    xaxis: {
      type: 'category' as const,
      categories,
      labels: { style: { colors: MUTED, fontSize: '10px' }, rotate: -20, trim: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickPlacement: 'on',
    },
    yaxis: {
      labels: { style: { colors: MUTED, fontSize: '10px' }, formatter: (v: number) => Utils.formatNumber(v, true, false) },
    },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    legend: { show: false },
    fill: { opacity: 0.85 },
    colors: palette,
    plotOptions: { bubble: { minBubbleRadius: 18, maxBubbleRadius: 60 } },
    tooltip: {
      custom: ({ seriesIndex, w }: any) => {
        const name = w?.config?.series?.[seriesIndex]?.name || categories[seriesIndex];
        const pt = w?.config?.series?.[seriesIndex]?.data?.[0];
        const val = Array.isArray(pt) ? pt[2] : 0;
        return `<div style="padding:6px 10px;font-size:11px;"><b>${name}</b><br/>${Utils.formatNumber(val, true, false)}</div>`;
      },
    },
  });

  const bubbleSeries = (names: string[], values: number[]) =>
    names.map((n, i) => ({ name: n, data: [[i, values[i] || 0, values[i] || 0]] }));

  const areaSplineOpts = (categories: string[], color: string) => ({
    chart: { type: 'area' as const, toolbar: { show: false }, fontFamily: 'inherit', sparkline: { enabled: false } },
    stroke: { curve: 'smooth' as const, width: 3, colors: [color] },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color, opacity: 0.45 },
          { offset: 100, color, opacity: 0.02 },
        ],
      },
    },
    markers: { size: 5, colors: [color], strokeColors: '#fff', strokeWidth: 2, hover: { size: 7 } },
    dataLabels: {
      enabled: true,
      formatter: (v: number) => Utils.formatNumber(v, true, false),
      offsetY: -8,
      style: { fontSize: '10px', fontWeight: 600, colors: [TEXT] },
      background: { enabled: false },
    },
    xaxis: {
      categories,
      labels: { style: { colors: MUTED, fontSize: '10px' }, rotate: -20, trim: true, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: MUTED, fontSize: '10px' }, formatter: (v: number) => Utils.formatNumber(v, true, false) } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    legend: { show: false },
    tooltip: { y: { formatter: (v: number) => Utils.formatNumber(v, true, false) } },
    colors: [color],
  });

  const stackedHorizOpts = (names: string[], palette: string[]) => ({
    chart: { type: 'bar' as const, stacked: true, stackType: '100%' as const, toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '55%',
        borderRadius: 6,
        borderRadiusApplication: 'around' as const,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(0)}%`,
      style: { fontSize: '10px', fontWeight: 700, colors: ['#fff'] },
      dropShadow: { enabled: false },
    },
    xaxis: { labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { show: false } },
    grid: { show: false, padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    legend: {
      position: 'bottom' as const,
      fontSize: '11px',
      labels: { colors: MUTED },
      markers: { width: 10, height: 10, radius: 12 } as any,
      itemMargin: { horizontal: 6, vertical: 2 },
    },
    colors: palette,
    tooltip: {
      y: {
        formatter: (val: number, opts: any) => {
          const raw = opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[0] || 0;
          return Utils.formatNumber(raw, true, false);
        },
      },
    },
    series: names,
  });

  const stackedHorizSeries = (names: string[], values: number[]) =>
    names.map((n, i) => ({ name: n, data: [values[i] || 0] }));

  const radialOpts = (labels: string[], palette: string[], total: number) => ({
    chart: { type: 'radialBar' as const, fontFamily: 'inherit', toolbar: { show: false } },
    labels,
    colors: palette,
    plotOptions: {
      radialBar: {
        offsetY: 0,
        startAngle: 0,
        endAngle: 270,
        hollow: { margin: 5, size: '38%', background: 'transparent' },
        track: { background: '#f1f5f9', strokeWidth: '100%', margin: 4 },
        dataLabels: {
          name: { show: true, fontSize: '11px', fontWeight: 600, color: MUTED, offsetY: -4 },
          value: {
            show: true,
            fontSize: '14px',
            fontWeight: 700,
            color: TEXT,
            offsetY: 4,
            formatter: () => Utils.formatNumber(total, true, false),
          },
          total: {
            show: true,
            label: 'Total',
            fontSize: '10px',
            fontWeight: 600,
            color: MUTED,
            formatter: () => Utils.formatNumber(total, true, false),
          },
        },
      },
    },
    stroke: { lineCap: 'round' as const },
    legend: {
      show: true,
      position: 'bottom' as const,
      fontSize: '10px',
      labels: { colors: MUTED },
      markers: { width: 8, height: 8, radius: 12 } as any,
      itemMargin: { horizontal: 4, vertical: 1 },
      formatter: (seriesName: string, opts: any) => {
        const val = opts?.w?.globals?.seriesTotals?.[opts.seriesIndex] || 0;
        return `${seriesName}: ${Utils.formatNumber((val / 100) * total, true, false)}`;
      },
    },
    tooltip: {
      y: { formatter: (v: number) => `${v.toFixed(1)}%` },
    },
  });

  const radialSeries = (values: number[], total: number) =>
    total > 0 ? values.map((v) => Number(((v / total) * 100).toFixed(2))) : values.map(() => 0);

  const revenuePalette = [ACCENT, '#22c55e', '#10b981', '#06b6d4', '#3b82f6'];
  const expensePalette = ['#ef4444', '#f97316', '#f59e0b', '#dc2626', '#b91c1c'];

  // Combined Rev/Exp — flatten into single series, color-code by type
  const combinedCats = [...revenueTop.names, ...expenseTop.names];
  const combinedValues = [...revenueTop.values, ...expenseTop.values];
  const combinedColors = [
    ...revenueTop.names.map(() => ACCENT),
    ...expenseTop.names.map(() => '#ef4444'),
  ];

  const combinedBarOpts = {
    chart: { type: 'bar' as const, toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '55%',
        borderRadius: 6,
        borderRadiusApplication: 'end' as const,
        distributed: true,
        dataLabels: { position: 'top' as const },
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: 'start' as const,
      offsetX: 40,
      formatter: (v: number) => `  ${Utils.formatNumber(v, true, false)}`,
      style: { fontSize: '11px', fontWeight: 700, colors: [TEXT] },
      background: { enabled: false },
      dropShadow: { enabled: false },
    },
    xaxis: {
      categories: combinedCats,
      min: 0,
      max: Math.ceil((Math.max(...combinedValues, 1) * 1.25) / 1000) * 1000,
      tickAmount: 7,
      labels: {
        style: { colors: MUTED, fontSize: '10px' },
        formatter: (v: any) => Utils.formatNumber(Math.round(Number(v)), true, false),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: TEXT, fontSize: '11px', fontWeight: 600 } } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    colors: combinedColors,
    fill: { opacity: 1 },
    tooltip: {
      y: { formatter: (v: number) => Utils.formatNumber(v, true, false) },
      custom: ({ dataPointIndex, w }: any) => {
        const name = w?.globals?.labels?.[dataPointIndex] || '';
        const val = w?.config?.series?.[0]?.data?.[dataPointIndex] || 0;
        const isRev = dataPointIndex < revenueTop.names.length;
        const color = isRev ? ACCENT : '#ef4444';
        // Find matching group in P&L breakdown
        const allGroups = isRev
          ? [...(pl?.revenue?.salesIncome?.breakdown || []), ...(pl?.revenue?.otherIncome?.breakdown || [])]
          : (pl?.operatingExpenses?.breakdown || []);
        const matchingGroup = allGroups.find((g: any) => (g.name || g.accountingCode) === name);
        return buildLedgerListTooltip(`${name} · ${isRev ? 'Revenue' : 'Expense'}`, val, color, matchingGroup ? [matchingGroup] : []);
      },
    },
  };

  const combinedBarSeries = [{ name: 'Amount', data: combinedValues }];

  const hasCombinedData = combinedCats.length > 0;

  const revenueTopTotal = revenueTop.values.reduce((s, v) => s + v, 0);
  const expenseTopTotal = expenseTop.values.reduce((s, v) => s + v, 0);

  const assetsGroupsBreak = topN(bs?.assets?.groups, 6);
  const liabGroupsBreak = topN(bs?.liabilities?.groups, 6);

  const palette = [ACCENT, '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

  // Build ledger tooltip HTML per group for donut hover
  const buildGroupTooltip = (groupName: string, groups: any[] | undefined, color: string, total: number) => {
    const g = (groups || []).find((x: any) => (x.name || x.accountingCode) === groupName);
    if (!g) return `<div style="padding:12px 14px;font-size:13px;font-family:${TT_FONT};"><b>${groupName}</b><br/>${Utils.formatNumber(total, true, false)}</div>`;
    return buildLedgerListTooltip(groupName, Number(g.total || total), color, [g]);
  };

  const assetsDonutOptions = {
    chart: { type: 'donut' as const, fontFamily: 'inherit' },
    labels: assetsGroupsBreak.names,
    colors: palette,
    legend: { position: 'bottom' as const, fontSize: '10px', labels: { colors: MUTED }, markers: { width: 8, height: 8, radius: 12 } as any, itemMargin: { horizontal: 4, vertical: 1 } },
    dataLabels: { enabled: true, style: { fontSize: '10px', fontWeight: 700, colors: ['#fff'] }, formatter: (val: number) => `${val.toFixed(0)}%` },
    stroke: { width: 2, colors: ['#fff'] },
    plotOptions: { pie: { donut: { size: '58%', labels: { show: false } } } },
    tooltip: {
      custom: ({ seriesIndex, w }: any) => {
        const groupName = w?.config?.labels?.[seriesIndex] || '';
        const color = w?.config?.colors?.[seriesIndex] || '#3b82f6';
        const value = w?.config?.series?.[seriesIndex] || 0;
        return buildGroupTooltip(groupName, bs?.assets?.groups, color, value);
      },
    },
  };
  const liabDonutOptions = {
    ...assetsDonutOptions,
    labels: liabGroupsBreak.names,
    colors: ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', ACCENT, '#06b6d4'],
    tooltip: {
      custom: ({ seriesIndex, w }: any) => {
        const groupName = w?.config?.labels?.[seriesIndex] || '';
        const color = w?.config?.colors?.[seriesIndex] || '#ef4444';
        const value = w?.config?.series?.[seriesIndex] || 0;
        return buildGroupTooltip(groupName, bs?.liabilities?.groups, color, value);
      },
    },
  };

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, backgroundColor: BG, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Financial Dashboard
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>
            {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} · {selectedAccount?.name || 'your account'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
          {/* Balance check pill */}
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 38,
            px: 1.5,
            borderRadius: 999,
            backgroundColor: isBalanced ? '#dcfce7' : isBalanced === false ? '#fee2e2' : SURFACE,
            border: `1px solid ${isBalanced ? '#86efac' : isBalanced === false ? '#fca5a5' : BORDER}`,
            boxSizing: 'border-box',
          }}>
            {isBalanced ? (
              <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />
            ) : isBalanced === false ? (
              <ErrorOutlineIcon sx={{ fontSize: 14, color: '#dc2626' }} />
            ) : (
              <AccountBalanceIcon sx={{ fontSize: 14, color: MUTED }} />
            )}
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: isBalanced ? '#15803d' : isBalanced === false ? '#b91c1c' : MUTED, lineHeight: 1 }}>
              {isBalanced == null ? 'No data' : isBalanced ? 'Books Balanced' : `Off by ${Utils.formatNumber(Math.abs(balanceDiff), true, false)}`}
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 999,
              p: 0.5,
              flex: { xs: 1, md: 'unset' },
              '& .MuiToggleButton-root': {
                border: 0,
                px: { xs: 1.25, sm: 2 },
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                color: MUTED,
                flex: { xs: 1, md: 'unset' },
                borderRadius: '999px !important',
                '&.Mui-selected': {
                  backgroundColor: TEXT,
                  color: '#fff',
                  '&:hover': { backgroundColor: TEXT },
                },
              },
            }}
          >
            <ToggleButton value="7days">7D</ToggleButton>
            <ToggleButton value="30days">30D</ToggleButton>
            <ToggleButton value="90days">90D</ToggleButton>
            <ToggleButton value="365days">1Y</ToggleButton>
            <ToggleButton value="fy">FY</ToggleButton>
          </ToggleButtonGroup>
          {rangeLabel && (
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              height: 38,
              px: 1.25,
              borderRadius: 999,
              backgroundColor: ACCENT_SOFT,
              border: `1px solid ${ACCENT_LIGHT}`,
              boxSizing: 'border-box',
            }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#365314', lineHeight: 1 }}>
                {rangeLabel}
              </Typography>
              <IconButton size="small" onClick={clearCustomRange} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 13, color: '#365314' }} />
              </IconButton>
            </Box>
          )}
          <IconButton
            onClick={(e) => setCalAnchor(e.currentTarget)}
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              '&:hover': { backgroundColor: ACCENT_SOFT },
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 18, color: TEXT }} />
          </IconButton>
          <Popover
            open={Boolean(calAnchor)}
            anchorEl={calAnchor}
            onClose={() => setCalAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, width: 240 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT }}>Custom range</Typography>
              <TextField
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={customStart ? fmtDate(customStart) : ''}
                onChange={(e) => setCustomStart(e.target.value ? new Date(e.target.value) : null)}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={customEnd ? fmtDate(customEnd) : ''}
                onChange={(e) => setCustomEnd(e.target.value ? new Date(e.target.value) : null)}
              />
              <Button
                variant="contained"
                size="small"
                onClick={applyCustomRange}
                disabled={!customStart || !customEnd}
                sx={{ backgroundColor: TEXT, '&:hover': { backgroundColor: '#1e293b' }, textTransform: 'none' }}
              >
                Apply
              </Button>
            </Box>
          </Popover>
        </Stack>
      </Stack>

      {errorMessage && (
        <Paper sx={{ ...cardSx, p: 1.25, mb: 1.5, borderColor: '#fca5a5', color: '#b91c1c', fontSize: '0.8rem' }}>{errorMessage}</Paper>
      )}

      {/* KPI Row */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Revenue"
            value={Utils.formatNumber(totalRevenue, true, false)}
            icon={<ReceiptLongIcon />}
            accent={ACCENT}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Expenses"
            value={Utils.formatNumber(totalExpenses, true, false)}
            icon={<CreditCardIcon />}
            accent="#ef4444"
            sub={{ positive: false, label: 'COGS + OpEx + Other' }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Net Profit"
            value={Utils.formatNumber(netProfit, true, false)}
            icon={<PaidIcon />}
            accent={netProfit >= 0 ? ACCENT : '#ef4444'}
            sub={netMargin ? { positive: netProfit >= 0, label: `${netMargin.toFixed(1)}% margin` } : null}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Assets"
            value={Utils.formatNumber(totalAssets, true, false)}
            icon={<AccountBalanceIcon />}
            accent="#3b82f6"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Liabilities"
            value={Utils.formatNumber(totalLiabilities, true, false)}
            icon={<SavingsIcon />}
            accent="#f59e0b"
            sub={{ positive: true, label: `Equity ${Utils.formatNumber(totalEquity, true, false)}` }}
          />
        </Grid>
      </Grid>

      {/* Row 1: P&L Flow + BS Composition */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>Profit & Loss Flow</Typography>
              <Typography sx={{ color: MUTED, fontSize: '0.7rem' }}>From journal entries</Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} /> : (
              <Chart options={plBarOptions} series={plBarSeries} type="bar" height={260} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>Balance Sheet</Typography>
              <Typography sx={{ color: MUTED, fontSize: '0.7rem' }}>
                {Utils.formatNumber(totalAssets + totalLiabilities + totalEquity, true, false)}
              </Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} /> :
              bsDonutSeries.every((v) => v === 0) ? (
                <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                <Chart options={bsDonutOptions} series={bsDonutSeries} type="donut" height={260} />
              )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 2: Combined Rev/Exp + Assets donut + Liab donut */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.8rem' }}>
                Revenue & Expenses
              </Typography>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ACCENT }} />
                  <Typography sx={{ color: MUTED, fontSize: '0.68rem' }}>Revenue</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                  <Typography sx={{ color: MUTED, fontSize: '0.68rem' }}>Expense</Typography>
                </Stack>
              </Stack>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} /> :
              !hasCombinedData ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.75rem' }}>No data</Box>
              ) : (
                <Chart options={combinedBarOpts} series={combinedBarSeries} type="bar" height={220} />
              )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.8rem' }}>
                Assets Breakdown
              </Typography>
              <Typography sx={{ color: '#3b82f6', fontSize: '0.78rem', fontWeight: 700 }}>
                {Utils.formatNumber(totalAssets, true, false)}
              </Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} /> :
              assetsGroupsBreak.values.length === 0 ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.75rem' }}>No data</Box>
              ) : (
                <Chart options={assetsDonutOptions} series={assetsGroupsBreak.values} type="donut" height={220} />
              )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.8rem' }}>
                Liabilities Breakdown
              </Typography>
              <Typography sx={{ color: '#f59e0b', fontSize: '0.78rem', fontWeight: 700 }}>
                {Utils.formatNumber(totalLiabilities, true, false)}
              </Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} /> :
              liabGroupsBreak.values.length === 0 ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.75rem' }}>No data</Box>
              ) : (
                <Chart options={liabDonutOptions} series={liabGroupsBreak.values} type="donut" height={220} />
              )}
          </Paper>
        </Grid>
      </Grid>

    </Box>
  );
};

export default CoaDashboard;
